import { useEffect, useMemo, useState } from 'react';
import type { EmailTemplate } from '@klaro7/shared-types';
import {
  Badge,
  Button,
  Input,
  Alert,
  AlertDescription,
  Spinner,
} from '@klaro7/ui';

import {
  useEmailTemplates,
  useUpdateEmailTemplate,
} from '@/features/settings/hooks/useEmailTemplates';
import { EmailEditor } from '@/features/settings/components/EmailEditor';
import { EmailScorePanel } from '@/features/settings/components/EmailScorePanel';
import { EmailPreview } from '@/features/settings/components/EmailPreview';
import { calculateEmailScore } from '@/features/settings/utils/emailScoring';

type Tab = 'editor' | 'preview';

export function AdminEmailTemplatesPage() {
  const { data: templates, isLoading } = useEmailTemplates();
  const updateMutation = useUpdateEmailTemplate();

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('editor');
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const selectedTemplate = templates?.find((t) => t.slug === selectedSlug) ?? null;

  // Score calculation (real-time)
  const score = useMemo(
    () =>
      calculateEmailScore(
        subject,
        body,
        selectedTemplate?.placeholders ?? [],
      ),
    [subject, body, selectedTemplate?.placeholders],
  );

  // Populate form when a template is selected
  useEffect(() => {
    if (selectedTemplate) {
      setSubject(selectedTemplate.subject);
      setBody(selectedTemplate.body);
      setIsActive(selectedTemplate.isActive);
      setActiveTab('editor');
    }
  }, [selectedTemplate]);

  // Auto-select first template when data loads
  useEffect(() => {
    if (templates && templates.length > 0 && !selectedSlug) {
      setSelectedSlug(templates[0]!.slug);
    }
  }, [templates, selectedSlug]);

  // Auto-dismiss alert
  useEffect(() => {
    if (!alert) return;
    const timer = setTimeout(() => setAlert(null), 4000);
    return () => clearTimeout(timer);
  }, [alert]);

  function handleSelectTemplate(template: EmailTemplate) {
    setSelectedSlug(template.slug);
    setSubject(template.subject);
    setBody(template.body);
    setIsActive(template.isActive);
  }

  async function handleSave() {
    if (!selectedSlug) return;
    try {
      await updateMutation.mutateAsync({
        slug: selectedSlug,
        data: { subject, body, isActive },
      });
      setAlert({ type: 'success', message: 'Template aggiornato con successo' });
    } catch {
      setAlert({ type: 'error', message: 'Errore nel salvataggio del template' });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Template Email</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Gestisci i template delle email inviate dalla piattaforma
        </p>
      </div>

      {alert && (
        <Alert variant={alert.type === 'success' ? 'success' : 'error'}>
          <AlertDescription>{alert.message}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Template list sidebar */}
        <div className="space-y-3 lg:col-span-1">
          <h2 className="text-sm font-semibold text-neutral-700">Template disponibili</h2>
          {templates?.map((template) => (
            <button
              key={template.slug}
              onClick={() => handleSelectTemplate(template)}
              className={`w-full rounded-xl border p-4 text-left transition-all ${
                selectedSlug === template.slug
                  ? 'border-brand-teal bg-brand-teal-50/50 ring-1 ring-brand-teal'
                  : 'border-neutral-200 bg-white hover:border-neutral-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-neutral-900">{template.name}</p>
                <Badge variant={template.isActive ? 'success' : 'default'} className="shrink-0">
                  {template.isActive ? 'Attivo' : 'Disattivato'}
                </Badge>
              </div>
              <p className="mt-1 truncate text-xs text-neutral-600">{template.subject}</p>
            </button>
          ))}
          {(!templates || templates.length === 0) && (
            <p className="text-sm text-neutral-600">Nessun template disponibile</p>
          )}
        </div>

        {/* Main editing area */}
        {selectedTemplate && (
          <div className="space-y-5 lg:col-span-3">
            <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
              {/* Header with template name + tabs */}
              <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
                <div className="flex items-center gap-3">
                  <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-700">
                      {selectedTemplate.name}
                    </h3>
                    <p className="text-xs text-neutral-600">
                      Slug: <code className="rounded bg-neutral-100 px-1 py-0.5">{selectedTemplate.slug}</code>
                    </p>
                  </div>
                </div>
                <Badge variant={isActive ? 'success' : 'default'}>
                  {isActive ? 'Attivo' : 'Disattivato'}
                </Badge>
              </div>

              {/* Tab bar */}
              <div className="flex gap-0 border-b border-neutral-100">
                <button
                  type="button"
                  onClick={() => setActiveTab('editor')}
                  className={`px-5 py-2.5 text-sm font-medium transition-colors ${
                    activeTab === 'editor'
                      ? 'border-b-2 border-brand-teal text-brand-teal'
                      : 'text-neutral-600 hover:text-neutral-700'
                  }`}
                >
                  Editor
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('preview')}
                  className={`px-5 py-2.5 text-sm font-medium transition-colors ${
                    activeTab === 'preview'
                      ? 'border-b-2 border-brand-teal text-brand-teal'
                      : 'text-neutral-600 hover:text-neutral-700'
                  }`}
                >
                  Anteprima
                </button>
              </div>

              <div className="p-5">
                {activeTab === 'editor' ? (
                  <div className="space-y-4">
                    {/* Subject with character counter */}
                    <div>
                      <Input
                        label="Oggetto"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Oggetto dell'email"
                      />
                      <p className={`mt-1 text-right text-xs ${
                        subject.length > 60 ? 'text-red-500' : 'text-neutral-600'
                      }`}>
                        {subject.length}/200
                      </p>
                    </div>

                    {/* WYSIWYG Editor */}
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                        Corpo
                      </label>
                      <EmailEditor
                        value={body}
                        onChange={setBody}
                        placeholders={selectedTemplate.placeholders}
                      />
                    </div>

                    {/* Placeholders reference */}
                    {selectedTemplate.placeholders.length > 0 && (
                      <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-600">
                          Placeholder disponibili
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedTemplate.placeholders.map((ph) => (
                            <span
                              key={ph.key}
                              className="inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-xs border border-neutral-200"
                            >
                              <code className="font-mono font-medium text-brand-teal-600">{ph.key}</code>
                              <span className="text-neutral-600">-</span>
                              <span className="text-neutral-600">{ph.description}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* isActive toggle */}
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
                      />
                      Template attivo
                    </label>

                    {/* Save button */}
                    <div className="flex justify-end border-t border-neutral-100 pt-4">
                      <Button
                        onClick={handleSave}
                        loading={updateMutation.isPending}
                      >
                        Salva Template
                      </Button>
                    </div>
                  </div>
                ) : (
                  <EmailPreview
                    subject={subject}
                    body={body}
                    placeholders={selectedTemplate.placeholders}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Score panel sidebar */}
        {selectedTemplate && (
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <EmailScorePanel score={score} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
